import React, { lazy, Suspense, useMemo } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import PrivateRoute from 'components/PrivateRoute';
import {
  ROLE_SUPER_ADMIN,
  ROLE_USER,
  ROLE_UCP_ADMIN,
  ROLE_CONTENT_ADMIN,
  ROLE_QUIZ_ADMIN,
  ROLE_TRAINING_ADMIN,
  ROLE_USER_ADMIN,
} from 'utils/roles';
import { buildSidebarTree } from 'services/CustomPages';
import flattenDeep from 'lodash/flattenDeep';
import useLogoutReferrer from 'components/PrivateRoute/useLogoutReferrer';
import CopyAdminDashboard from './routes/EngageAdmin/LearningPath/Copy';

const LoginPage = lazy(() => import('routes/Login'));
const LogoutPage = lazy(() => import('routes/Logout'));
const Dashboard = lazy(() => import('routes/Dashboard'));
const Profile = lazy(() => import('routes/Profile'));
const Register = lazy(() => import('routes/Register'));
const ForgotPassword = lazy(() => import('routes/ForgotPassword'));
const TermsAndConditions = lazy(() => import('routes/TermsAndConditions'));
const QuizHistory = lazy(() => import('routes/QuizHistory'));
const Events = lazy(() => import('routes/Events'));
const Module = lazy(() => import('routes/Module'));
const OnlineClassroom = lazy(() => import('routes/Activities/OnlineClassroom'));
const QuizIntroPage = lazy(() => import('routes/Activities/Quizzes'));
const QuizQuestion = lazy(() => import('routes/Activities/Quizzes/QuizQuestion'));
const QuizResults = lazy(() => import('routes/Activities/Quizzes/QuizResults'));
const TrainingAdminDashboard = lazy(() => import('routes/EngageAdmin/Dashboard'));
const CreateLearningCampaign = lazy(() => import('routes/EngageAdmin/LearningCampaign/Create'));
const EditLearningCampaign = lazy(() => import('routes/EngageAdmin/LearningCampaign/Edit'));
const CreateLearningPath = lazy(() => import('routes/EngageAdmin/LearningPath/Create'));
const EditLearningPath = lazy(() => import('routes/EngageAdmin/LearningPath/Edit'));
const DiscussionForum = lazy(() => import('routes/DiscussionForum'));
const IsbeSurvey = lazy(() => import('routes/IsbeSurvey'));
const CustomPage = lazy(() => import('routes/CustomPage'));
const NotFoundPage = lazy(() => import('routes/ErrorPages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('routes/ErrorPages/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('routes/ErrorPages/ServerErrorPage'));

const allRoles = [
  ROLE_SUPER_ADMIN,
  ROLE_USER,
  ROLE_UCP_ADMIN,
  ROLE_CONTENT_ADMIN,
  ROLE_QUIZ_ADMIN,
  ROLE_TRAINING_ADMIN,
  ROLE_USER_ADMIN,
];

function Routes({ customPages }) {
  useLogoutReferrer();

  const rootCustomPages = useMemo(() => customPages.filter(page => page.parent === null), [customPages]);
  const sidebarTree = useMemo(() => buildSidebarTree(rootCustomPages, customPages), [rootCustomPages, customPages]);

  // Flatten so we can iterate over the array for the routes,
  // but still have menuRoot for use with the sidebar.
  const flattenedTree = useMemo(
    () => flattenDeep(sidebarTree.map(root => [root, ...root.children.map(child => [child, ...child.children])])),
    [sidebarTree]
  );

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <PrivateRoute path='/' component={Dashboard} exact roles={allRoles} />
        <PrivateRoute path='/dashboard' component={Dashboard} exact roles={allRoles} />
        <PrivateRoute path='/online-classroom' component={OnlineClassroom} exact roles={allRoles} />
        <Route path='/module/:moduleId' component={Module} exact roles={allRoles} />
        <PrivateRoute path='/quiz/:moduleId/intro' component={QuizIntroPage} exact roles={allRoles} />
        <PrivateRoute path='/quiz/:moduleId/questions' component={QuizQuestion} exact roles={allRoles} />
        <PrivateRoute path='/quiz/:moduleId/results' component={QuizResults} exact roles={allRoles} />
        <PrivateRoute path='/quiz-history' component={QuizHistory} exact roles={allRoles} />
        <PrivateRoute path='/events' component={Events} exact roles={allRoles} requiresContract={true} />
        <PrivateRoute path='/profile' component={Profile} exact roles={allRoles} />
        <PrivateRoute
          path='/learning-campaign/:learningCampaignId/forum'
          component={DiscussionForum}
          exact
          roles={allRoles}
          requiresContract={true}
        />
        <PrivateRoute path='/isbe-survey/:quizAttemptId' component={IsbeSurvey} exact roles={allRoles} />

        {/* Engage Admin Routes */}
        <PrivateRoute
          path='/engage-admin'
          component={TrainingAdminDashboard}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />
        <PrivateRoute
          path='/engage-admin/learning-path/create'
          component={CreateLearningPath}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />
        <PrivateRoute
          path='/engage-admin/learning-path/edit/:id'
          component={EditLearningPath}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />
        <PrivateRoute
          path='/engage-admin/inf-learning-template/edit/:id'
          component={CopyAdminDashboard}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />
        <PrivateRoute
          path='/engage-admin/learning-campaign/create'
          component={CreateLearningCampaign}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />
        <PrivateRoute
          path='/engage-admin/learning-campaign/edit/:learningCampaignId'
          component={EditLearningCampaign}
          exact
          roles={[ROLE_SUPER_ADMIN, ROLE_UCP_ADMIN, ROLE_TRAINING_ADMIN]}
          requiresContract={true}
        />

        <Route path='/register' component={Register} exact />
        <Route path='/login' component={LoginPage} exact />
        <Route path='/logout' component={LogoutPage} exact />
        <Route path='/forgot-password/:resetToken?' component={ForgotPassword} exact />
        <Route path='/terms-and-conditions' component={TermsAndConditions} exact />

        {/* custom pages */}
        {flattenedTree.map(page => {
          return (
            <Route
              key={`page-${page.slug}`}
              path={page.url}
              render={props => (
                <CustomPage
                  {...props}
                  pageId={page.id}
                  title={page.title}
                  isInfinitextAccessPage={page.isInfinitextAccessPage}
                  mainBody={page.customBody}
                  rightRail={page.customRightRail}
                  sidebarKey={page.menuRoot || page.id}
                  sidebarTree={sidebarTree}
                />
              )}
              exact
            />
          );
        })}

        {/* error pages */}
        <Route path='/404' component={NotFoundPage} />
        <Route path='/403' component={ForbiddenPage} />
        <Route path='/500' component={ServerErrorPage} />
        <Redirect to='/404' />
      </Switch>
    </Suspense>
  );
}

export default Routes;
